import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import '../assets/styles/ParentDashboard.css';
import '../assets/styles/GoalsTracking.css';
import ProgressReports from './ProgressReports';
import ParentChoreManagement from './ParentChoreManagement';
import AvatarPicker from './AvatarPicker';
import StreakRewardSettings from './StreakRewardSettings';
import PayoutManager from './PayoutManager';
import PayPalDonateButton from './PayPalDonateButton';
import AllowanceManager from './AllowanceManager';
import SmartStore from './SmartStore';
import QuestManager from './QuestManager';
import AdminQuestDashboard from './AdminQuestDashboard';
import EmailTemplateEditor from './EmailTemplateEditor';
import EmailPreferences from './EmailPreferences';
import WebOnboardingWizard from './WebOnboardingWizard';
import ParentPinModal from './ParentPinModal';
import ChoreSuggestions from './ChoreSuggestions';

// Modern SVG Icons for Action Cards
const IconChore = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 14L11 16L15 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconGoal = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" opacity="0.5" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
        <path d="M12 3V5M12 19V21M3 12H5M19 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const IconManage = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" opacity="0.6" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" opacity="0.6" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
    </svg>
);

const IconChild = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
        <path d="M17 3.13C17.8604 3.35031 18.623 3.8506 19.1676 4.55232C19.7122 5.25404 20.0078 6.11683 20.0078 7.005C20.0078 7.89317 19.7122 8.75596 19.1676 9.45768C18.623 10.1594 17.8604 10.6597 17 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
    </svg>
);

const IconWave = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="#6366F1" opacity="0.1" />
        <path d="M19 13.5C19 14.5 17.5 15 16 15C14.5 15 14 14.5 13 13.5" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" />
        <path d="M7 10C7 10 8 9 10 9C12 9 13 10 13 10" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" />
        <path d="M12 12C12 13.1046 11.1046 14 10 14C8.89543 14 8 13.1046 8 12C8 10.8954 8.89543 10 10 10C11.1046 10 12 10.8954 12 12Z" fill="#6366F1" />
    </svg>
);

const ParentDashboard = ({ user, onLogout }) => {
    const [isAddChildModalOpen, setIsAddChildModalOpen] = useState(false);
    const [childFormData, setChildFormData] = useState({
        name: '',
        username: '',
        password: '',
        avatar: '😊',
        coppaConsent: false
    });
    const [generatedCredentials, setGeneratedCredentials] = useState(null);
    const [childError, setChildError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [childrenCount, setChildrenCount] = useState(0);    // Basic States
    const [kids, setKids] = useState([]);
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(true);

    // Fetch theme preference
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme') || 'light';
        document.documentElement.setAttribute('data-theme', savedTheme);
    }, []);

    const toggleTheme = () => {
        const currentTheme = document.documentElement.getAttribute('data-theme');
        const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
        document.documentElement.setAttribute('data-theme', newTheme);
        localStorage.setItem('theme', newTheme);
    };
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [resetTarget, setResetTarget] = useState(null); // { id: '...', username: '...' }
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');

    // Bonus Award State
    const [isBonusModalOpen, setIsBonusModalOpen] = useState(false);
    const [bonusTarget, setBonusTarget] = useState(null); // { id, name }
    const [bonusAmount, setBonusAmount] = useState('');
    const [bonusReason, setBonusReason] = useState('');
    const [bonusError, setBonusError] = useState('');

    // Feedback State
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [feedbackType, setFeedbackType] = useState('feature');
    const [feedbackTitle, setFeedbackTitle] = useState('');
    const [feedbackDescription, setFeedbackDescription] = useState('');
    const [feedbackError, setFeedbackError] = useState('');
    const [feedbackSuccess, setFeedbackSuccess] = useState('');

    // Friend Requests State
    const [pendingFriendRequests, setPendingFriendRequests] = useState([]);

    // Chore State
    const [isCreateChoreModalOpen, setIsCreateChoreModalOpen] = useState(false);
    const [choreFormData, setChoreFormData] = useState({
        title: '',
        description: '',
        reward_amount: 5,
        recurrence: 'daily',
        requires_evidence: false,
        kid_ids: [], // Array of kid IDs to assign this chore to
        depends_on_quest_id: '', // Optional: Linked prerequisite quest
    });

    // Goal State
    const [isSetGoalModalOpen, setIsSetGoalModalOpen] = useState(false);
    const [goalFormData, setGoalFormData] = useState({
        kid_user_id: '',
        title: '',
        target_amount: 50,
    });

    // Stats
    const [choresCount, setChoresCount] = useState(0);

    // Settings View State
    const [activeSettingsTab, setActiveSettingsTab] = useState('general'); // 'general', 'streaks', etc.
    const [goalsCount, setGoalsCount] = useState(0);
    const [totalPaidOut, setTotalPaidOut] = useState(0);
    const [goals, setGoals] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [pendingSubmissions, setPendingSubmissions] = useState(0);
    const [challenges, setChallenges] = useState([]);

    // View State
    const [showChoreManagement, setShowChoreManagement] = useState(false);

    // Email Preferences State
    const [emailPrefs, setEmailPrefs] = useState({
        weekly_digest: true,
        chore_reminders: true,
        marketing: true
    });
    const [isSavingPrefs, setIsSavingPrefs] = useState(false);
    const userKey = user?.user_id || user?.id || 'default';
    const onboardingKey = `dadbucks_web_onboarding_complete_${userKey}`;
    const parentPinKey = `dadbucks_parent_pin_${userKey}`;
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [pinGate, setPinGate] = useState({
        open: false,
        title: 'Parent PIN Required',
        description: 'Enter your PIN to continue.',
        storedPin: '',
        action: null,
    });

    useEffect(() => {
        if (!user?.role) return;
        if ((user.role === 'parent' || user.role === 'admin') && !localStorage.getItem(onboardingKey)) {
            setShowOnboarding(true);
        }
    }, [onboardingKey, user?.role]);

    const runProtectedAction = useCallback((action, title = 'Parent PIN Required', description = 'Enter your 4-digit PIN to continue.') => {
        const storedPin = localStorage.getItem(parentPinKey);
        if (!storedPin) {
            action();
            return;
        }

        setPinGate({
            open: true,
            title,
            description,
            storedPin,
            action,
        });
    }, [parentPinKey]);

    // Initial Data Fetch
    useEffect(() => {
        if (!user) return;

        const fetchData = async () => {
            try {
                // Determine tenant ID logic
                const tenantId = user.tenant_id || user.household_id;

                const [choresRes, , membersRes, emailPrefsRes] = await Promise.all([
                    axios.get(`/api/v1/chores?household_id=${tenantId}`),
                    axios.get(`/api/v1/allowances?household_id=${tenantId}`), // Example fallback
                    axios.get(`/api/v1/users/kids?tenant_id=${tenantId}`),
                    axios.get('/api/v1/email/preferences').catch(() => null)
                ]);

                setChallenges(choresRes.data || []);
                setChoresCount((choresRes.data || []).length);
                const normalizedMembers = (membersRes.data || []).map(kid => ({
                    ...kid,
                    id: kid.ID || kid.id,
                    ID: kid.ID || kid.id,
                    DisplayName: kid.DisplayName || kid.display_name || kid.Username || kid.username || '',
                    Username: kid.Username || kid.username || '',
                    Avatar: kid.Avatar || kid.avatar || '👶'
                }));
                const kidsWithBalances = await Promise.all(
                    normalizedMembers.map(async (kid) => {
                        try {
                            const balanceRes = await axios.get(`/api/v1/balance?kid_id=${kid.ID}`);
                            return { ...kid, balance: balanceRes.data.balance || 0 };
                        } catch (err) {
                            console.error('Failed to fetch balance for kid:', kid, err);
                            return { ...kid, balance: 0 };
                        }
                    })
                );
                setKids(kidsWithBalances);

                if (emailPrefsRes && emailPrefsRes.data) {
                    setEmailPrefs(prev => ({
                        weekly_digest: emailPrefsRes.data.weekly_digest ?? true,
                        chore_reminders: emailPrefsRes.data.chore_reminders ?? true,
                        marketing: prev.marketing // Backend doesn't store this, keeping local state
                    }));
                }

                // ... the rest of the original logic if needed
            } catch (err) {
                console.error('Failed to fetch initial dashboard data:', err);
            }
        };

        fetchData();
    }, [user]);

    const fetchKids = useCallback(async () => {
        try {
            const response = await axios.get(`/api/v1/users/kids?tenant_id=${user.tenant_id}`);

            const kidsData = response.data || [];

            // Normalize kid data - backend uses uppercase ID, frontend expects lowercase id
            const normalizedKids = kidsData.map(kid => ({
                ...kid,
                id: kid.ID || kid.id,  // Ensure lowercase id exists
                ID: kid.ID || kid.id,   // Keep uppercase ID for compatibility
                DisplayName: kid.DisplayName || kid.display_name || kid.Username || kid.username || '',
                Username: kid.Username || kid.username || '',
                Avatar: kid.Avatar || kid.avatar || '👶'
            }));


            const kidsWithBalances = await Promise.all(
                normalizedKids.map(async (kid) => {
                    try {
                        const balanceRes = await axios.get(`/api/v1/balance?kid_id=${kid.ID}`);
                        return { ...kid, balance: balanceRes.data.balance || 0 };
                    } catch (err) {
                        console.error('❌ fetchKids: Failed to fetch balance for kid:', kid, err);
                        return { ...kid, balance: 0 };
                    }
                })
            );
            setKids(kidsWithBalances);
            setChildrenCount(kidsWithBalances.length);
        } catch (err) {
            console.error('❌ fetchKids: Error fetching kids:', err);
            console.error('❌ fetchKids: Error details:', err.response?.data);
        }
    }, [user.tenant_id]);

    const fetchStats = useCallback(async () => {
        try {
            const choresResponse = await axios.get(`/api/v1/chores?household_id=${user.tenant_id}`);
            const choresData = choresResponse.data || [];
            setChoresCount(choresData.length);
            setChallenges(choresData);

            const goalsResponse = await axios.get(`/api/v1/goals?household_id=${user.tenant_id}`);
            const goalsData = goalsResponse.data || [];
            setGoalsCount(goalsData.length);

            // Enrich goals with kid information and balance
            const goalsWithKidInfo = await Promise.all(
                goalsData.map(async (goal) => {
                    const kid = kids.find(k => (k.ID || k.id) === goal.kid_user_id);
                    return {
                        ...goal,
                        kidName: kid?.Name || kid?.Username || 'Unknown',
                        currentBalance: kid?.balance || 0
                    };
                })
            );
            setGoals(goalsWithKidInfo);

            // Fetch total paid out
            const payoutResponse = await axios.get(`/api/v1/payouts?household_id=${user.tenant_id}`);
            const payouts = payoutResponse.data || [];
            const total = payouts.reduce((sum, p) => sum + (p.amount || 0), 0);
            setTotalPaidOut(total);
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    }, [user.tenant_id, kids]);

    const fetchSubmissions = useCallback(async () => {
        if (!user?.tenant_id) return;
        try {
            const response = await axios.get(`/api/v1/submissions?household_id=${user.tenant_id}`);
            const allSubmissions = response.data || [];
            setSubmissions(allSubmissions);
            setPendingSubmissions(allSubmissions.filter(s => s.status === 'pending').length);
        } catch (err) {
            console.error('Failed to fetch submissions:', err);
        }
    }, [user?.tenant_id]);


    useEffect(() => {
        fetchKids();

        // Poll for updates every 10 seconds
        const pollInterval = setInterval(() => {
            fetchKids();
        }, 10000);

        return () => clearInterval(pollInterval);
    }, [fetchKids]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        fetchSubmissions();

        // Poll for submission updates every 10 seconds
        const pollInterval = setInterval(() => {
            fetchSubmissions();
        }, 10000);

        return () => clearInterval(pollInterval);
    }, [fetchSubmissions]);

    // Fetch pending friend requests for kids in this household
    const fetchPendingFriendRequests = useCallback(async () => {
        try {
            const response = await axios.get('/api/v1/friends/pending');
            setPendingFriendRequests(response.data || []);
        } catch (error) {
            console.error('Failed to fetch pending friend requests:', error);
        }
    }, []);

    useEffect(() => {
        fetchPendingFriendRequests();
    }, [fetchPendingFriendRequests]);

    const handleApproveFriendRequest = async (requestId) => {
        try {
            await axios.post(`/api/v1/friends/${requestId}/approve`);
            fetchPendingFriendRequests();
        } catch (error) {
            console.error('Failed to approve friend request:', error);
        }
    };

    const handleDeclineFriendRequest = async (requestId) => {
        try {
            await axios.post(`/api/v1/friends/${requestId}/decline`);
            fetchPendingFriendRequests();
        } catch (error) {
            console.error('Failed to decline friend request:', error);
        }
    };

    const handleKidToggle = (kidId) => {
        const currentIds = choreFormData.kid_ids;
        if (currentIds.includes(kidId)) {
            // Remove kid from assignment
            setChoreFormData({
                ...choreFormData,
                kid_ids: currentIds.filter(id => id !== kidId)
            });
        } else {
            // Add kid to assignment
            setChoreFormData({
                ...choreFormData,
                kid_ids: [...currentIds, kidId]
            });
        }
    };

    const handleCreateChore = async (e) => {
        e.preventDefault();
        try {
            // If no specific kids selected, assign to all kids (if any exist)
            const kidIds = choreFormData.kid_ids.length > 0
                ? choreFormData.kid_ids
                : kids.map(k => (k.ID || k.id).toString());

            await axios.post('/api/v1/chores', {
                ...choreFormData,
                reward_amount: choreFormData.reward_amount * 100, // Convert dollars to cents
                household_id: user.tenant_id,
                kid_ids: kidIds, // Will be empty array if no kids exist yet
                depends_on_quest_id: choreFormData.depends_on_quest_id || undefined
            });
            alert('Chore created successfully!');
            setIsCreateChoreModalOpen(false);
            setChoreFormData({ title: '', description: '', reward_amount: 5, recurrence: 'daily', requires_evidence: false, kid_ids: [], depends_on_quest_id: '' });
            fetchStats();
        } catch (err) {
            console.error('Failed to create chore:', err);
            alert('Failed to create chore');
        }
    };

    const handleSetGoal = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/api/v1/goals', {
                ...goalFormData,
                target_amount: goalFormData.target_amount * 100, // Convert dollars to cents
                household_id: user.tenant_id
            });
            alert('Goal set successfully!');
            setIsSetGoalModalOpen(false);
            setGoalFormData({ kid_user_id: '', title: '', target_amount: 50 });
            fetchStats();
        } catch (err) {
            console.error('Failed to set goal:', err);
            alert('Failed to set goal');
        }
    };

    const handleApproveSubmission = async (submissionId) => {
        runProtectedAction(async () => {
            try {
                await axios.post('/api/v1/submissions/approve', {
                    submission_id: submissionId,
                    approved: true,
                    parent_user_id: user.user_id,
                    notes: 'Approved'
                });
                fetchSubmissions();
                fetchKids();
            } catch (err) {
                console.error('Failed to approve submission:', err);
                alert('Failed to approve submission');
            }
        }, 'Approve Submission', 'Enter your Parent PIN before approving this submission.');
    };

    const handleRejectSubmission = async (submissionId) => {
        runProtectedAction(async () => {
            try {
                await axios.post('/api/v1/submissions/reject', {
                    submission_id: submissionId,
                    parent_user_id: user.user_id,
                    notes: 'Rejected'
                });
                fetchSubmissions();
            } catch (err) {
                console.error('Failed to reject submission:', err);
                alert('Failed to reject submission');
            }
        }, 'Reject Submission', 'Enter your Parent PIN before rejecting this submission.');
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setPasswordError('');

        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        // Validate password length
        if (newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters');
            return;
        }

        try {
            await axios.post('/api/v1/users/reset-password', {
                user_id: resetTarget.id,
                password: newPassword
            });
            alert(`Password reset for ${resetTarget.username}`);
            setIsResetModalOpen(false);
            setNewPassword('');
            setConfirmPassword('');
            setPasswordError('');
        } catch (err) {
            setPasswordError('Failed to reset password');
        }
    };


    // Generate secure random password
    const generatePassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
        let password = '';
        for (let i = 0; i < 8; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return password;
    };

    // Generate username from name
    const generateUsername = (name) => {
        return name.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
    };


    const handleAddChild = async (e) => {
        e.preventDefault();
        setChildError('');
        setIsSubmitting(true);

        try {
            // Generate credentials
            const username = childFormData.username || generateUsername(childFormData.name);
            const password = childFormData.password || generatePassword();

            const response = await axios.post('/api/v1/auth/register-kid', {
                name: childFormData.name,
                username: username,
                password: password,
                household_id: user.tenant_id,
                avatar: childFormData.avatar || '😊',
                coppa_consent: childFormData.coppaConsent
            });

            // Store credentials to show to parent
            setGeneratedCredentials({
                name: childFormData.name,
                username: username,
                password: password
            });

            setChildFormData({ name: '', username: '', password: '', coppaConsent: false });
            setChildrenCount(prev => prev + 1);

            // Wait a moment for backend to process, then refresh
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Force a state update by clearing kids first
            setKids([]);
            await fetchKids();

            // Don't close modal yet - show credentials first
        } catch (err) {
            console.error('Failed to add child:', err);
            setChildError(err.response?.data?.message || 'Failed to add child');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAwardBonus = async (e) => {
        e.preventDefault();
        setBonusError('');

        const amount = parseFloat(bonusAmount);
        if (isNaN(amount) || amount <= 0) {
            setBonusError('Please enter a valid positive amount');
            return;
        }

        if (!bonusReason.trim()) {
            setBonusError('Please enter a reason for the bonus');
            return;
        }

        runProtectedAction(async () => {
            try {
                await axios.post('/api/v1/bonus/surprise', {
                    kid_user_id: bonusTarget.id,
                    amount: Math.round(amount * 100), // Convert dollars to cents
                    reason: bonusReason.trim(),
                    emoji: '🎁'
                });

                // Show success with confetti-style message
                alert(`🎉 Surprise! $${amount.toFixed(2)} bonus awarded to ${bonusTarget.name}!\n\nReason: ${bonusReason}`);
                setIsBonusModalOpen(false);
                setBonusAmount('');
                setBonusReason('');
                setBonusTarget(null);
                // Small delay to ensure database transaction is committed before fetching
                setTimeout(() => fetchKids(), 500);
            } catch (err) {
                console.error('Failed to award bonus:', err);
                setBonusError(err.response?.data || 'Failed to award bonus');
            }
        }, 'Award Surprise Bonus', 'Enter your Parent PIN before sending a surprise bonus.');
    };

    if (showChoreManagement) {
        return <ParentChoreManagement user={user} onBack={() => setShowChoreManagement(false)} />;
    }

    return (
        <div className="dashboard-container">
            {showOnboarding && (
                <WebOnboardingWizard
                    user={user}
                    onKidAdded={fetchKids}
                    onComplete={() => setShowOnboarding(false)}
                />
            )}
            <ParentPinModal
                open={pinGate.open}
                title={pinGate.title}
                description={pinGate.description}
                storedPin={pinGate.storedPin}
                onClose={() => setPinGate(prev => ({ ...prev, open: false, action: null }))}
                onSuccess={() => {
                    const action = pinGate.action;
                    setPinGate(prev => ({ ...prev, open: false, action: null }));
                    if (action) {
                        action();
                    }
                }}
            />
            <header className="dashboard-header">
                <div className="header-left">
                    <h1>Parent Dashboard</h1>
                    <p className="subtitle">Manage your family's chores and rewards</p>
                </div>
                <div className="header-right">
                    {user?.role === 'admin' && (
                        <button
                            className="btn-admin-switch"
                            onClick={() => window.location.href = '/admin'}
                            style={{
                                marginRight: '15px',
                                background: 'rgba(99, 102, 241, 0.1)',
                                border: '1px solid #6366f1',
                                color: '#6366f1',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                            }}
                        >
                            🛡️ Admin Panel
                        </button>
                    )}
                    <button
                        className="btn-icon"
                        onClick={toggleTheme}
                        style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', marginRight: '15px' }}
                        title="Toggle Dark Mode"
                        aria-label="Toggle dark mode"
                    >
                        🌙
                    </button>
                    <div className="notification-bell" style={{ position: 'relative', marginRight: '20px', cursor: 'pointer', fontSize: '1.2rem' }} onClick={() => setHasUnreadNotifications(false)} role="button" tabIndex={0} aria-label={hasUnreadNotifications ? 'Notifications (unread)' : 'Notifications'} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setHasUnreadNotifications(false); }}>
                        🔔
                        {hasUnreadNotifications && (
                            <span className="unread-dot" style={{
                                position: 'absolute',
                                top: '0px',
                                right: '-2px',
                                width: '10px',
                                height: '10px',
                                backgroundColor: '#ef4444',
                                borderRadius: '50%',
                                border: '2px solid white'
                            }}></span>
                        )}
                    </div>
                    <div className="user-badge">
                        <div className="user-avatar">{user?.username?.[0]?.toUpperCase() || 'P'}</div>
                        <span>{user?.username}</span>
                        <button onClick={onLogout} className="btn-logout" style={{ marginLeft: '10px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.9rem' }} aria-label="Log out">Logout</button>
                    </div>
                </div>
            </header>

            <div className="dashboard-content">
                {/* Sidebar with PayPal Donate Button (Free Tier Only) */}
                <aside className="dashboard-sidebar">
                    {user?.subscription_tier !== 'premium' && <PayPalDonateButton />}
                </aside>

                {/* Main Content Area */}
                <div className="dashboard-main">
                    <div className="stats-bar">
                        <div className="stat-item">
                            <div className="stat-label">FAMILY MEMBERS</div>
                            <div className="stat-value">{1 + childrenCount}</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-label">ACTIVE CHORES</div>
                            <div className="stat-value">{choresCount}</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-label">TOTAL PAID OUT</div>
                            <div className="stat-value">${(totalPaidOut / 100).toFixed(2)}</div>
                        </div>
                    </div>

                    {/* Friend Requests Notification */}
                    {pendingFriendRequests.length > 0 && (
                        <div className="notification-banner" style={{
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            borderRadius: '12px',
                            padding: '16px 20px',
                            marginBottom: '24px',
                            color: 'white'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                                    👥 Friend Requests ({pendingFriendRequests.length})
                                </h3>
                            </div>
                            {pendingFriendRequests.map(request => (
                                <div key={request.id} style={{
                                    background: 'rgba(255,255,255,0.15)',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    marginBottom: '8px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div>
                                        <span style={{ fontSize: '1.2rem', marginRight: '8px' }}>{request.requester_avatar || '👤'}</span>
                                        <strong>{request.requester_name}</strong>
                                        <span style={{ opacity: 0.8 }}> wants to be friends with </span>
                                        <strong>{request.receiver_name}</strong>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={() => handleApproveFriendRequest(request.id)}
                                            aria-label={`Approve friend request from ${request.requester_name}`}
                                            style={{
                                                background: '#22c55e',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '6px 12px',
                                                cursor: 'pointer',
                                                fontWeight: '600'
                                            }}
                                        >
                                            ✓ Approve
                                        </button>
                                        <button
                                            onClick={() => handleDeclineFriendRequest(request.id)}
                                            aria-label={`Decline friend request from ${request.requester_name}`}
                                            style={{
                                                background: 'rgba(255,255,255,0.2)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '6px 12px',
                                                cursor: 'pointer',
                                                fontWeight: '600'
                                            }}
                                        >
                                            ✗ Decline
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Progress Report - New API-driven component */}
                    {kids.length > 0 && (
                        <section className="family-management">
                            <ProgressReports householdId={user.tenant_id} userRole="parent" />
                        </section>
                    )}
                    {/* AI Chore Suggestions */}
                    {kids.length > 0 && (
                        <section className="family-management">
                            <ChoreSuggestions
                                kidId={kids[0]?.ID || kids[0]?.id}
                                onCreateChore={(suggestion) => {
                                    setChoreFormData({
                                        title: suggestion.title,
                                        description: suggestion.description,
                                        reward_amount: suggestion.reward_min,
                                        recurrence: 'daily',
                                        requires_evidence: false,
                                        kid_ids: [],
                                        depends_on_quest_id: '',
                                    });
                                    setIsCreateChoreModalOpen(true);
                                }}
                            />
                        </section>
                    )}

                    {/* Payout Manager - Track offline cash payments */}
                    {kids.length > 0 && (
                        <section className="family-management">
                            <PayoutManager householdId={user.tenant_id} />
                        </section>
                    )}

                    {/* Quest Manager - Weekly Quests */}
                    {kids.length > 0 && (
                        <section className="family-management">
                            <QuestManager householdKids={kids} onQuestCreated={fetchStats} />
                        </section>
                    )}

                    <section className="onboarding-card">
                        <div className="welcome-icon-wrapper">
                            <IconWave />
                        </div>
                        <h2>Welcome, {user?.username}!</h2>
                        <p>
                            {childrenCount === 0
                                ? "Your household is ready to go. The next step is to add your kids and start teaching them the value of DadBucks."
                                : childrenCount > 0 && choresCount === 0
                                    ? "Great! You've added your family members. Now create some chores to help your kids earn DadBucks."
                                    : choresCount > 0 && goalsCount === 0
                                        ? "Awesome! Your kids can now earn DadBucks. Set a savings goal to help them learn to save for something special."
                                        : "Your household is all set up! Your kids can earn, save, and work toward their goals. Keep the momentum going!"
                            }
                        </p>

                        <div className="onboarding-grid">
                            {/* Smart ordering: prioritize Add Child when household has 0-1 kids */}
                            {childrenCount < 2 && (
                                <div className="action-card action-family" onClick={() => setIsAddChildModalOpen(true)}>
                                    <div className="action-icon-wrapper">
                                        <IconChild />
                                    </div>
                                    <h3>Add a Child</h3>
                                    <p>Create an account for your child. They'll get their own login to see their chores and balances.</p>
                                    <button className="btn-action">Add Family Member</button>
                                </div>
                            )}

                            <div className="action-card action-chore" onClick={() => setIsCreateChoreModalOpen(true)}>
                                <div className="action-icon-wrapper">
                                    <IconChore />
                                </div>
                                <h3>{choresCount === 0 ? 'Create First Chore' : 'Create New Chore'}</h3>
                                <p>Set up a task like "Clean Room" or "Mow Lawn" and decide how many DadBucks it's worth.</p>
                                <button className="btn-action">{choresCount === 0 ? 'Create Challenge' : 'Add Chore'}</button>
                            </div>

                            <div className="action-card action-goal" onClick={() => setIsSetGoalModalOpen(true)}>
                                <div className="action-icon-wrapper">
                                    <IconGoal />
                                </div>
                                <h3>Set a Goal</h3>
                                <p>Help your child save for something big. Set a savings target to motivate them.</p>
                                <button className="btn-action">Set Goal</button>
                            </div>

                            <div className="action-card action-manage" onClick={() => setShowChoreManagement(true)}>
                                <div className="action-icon-wrapper">
                                    <IconManage />
                                </div>
                                <h3>Manage Chores</h3>
                                <p>View all chores with filters for active, pending approval, and completed items.</p>
                                <button className="btn-action">View All Chores</button>
                            </div>

                            {/* Move Add Child to end when household has 2+ kids */}
                            {childrenCount >= 2 && (
                                <div className="action-card action-family" onClick={() => setIsAddChildModalOpen(true)}>
                                    <div className="action-icon-wrapper">
                                        <IconChild />
                                    </div>
                                    <h3>Add a Child</h3>
                                    <p>Create an account for your child. They'll get their own login to see their chores and balances.</p>
                                    <button className="btn-action">Add Family Member</button>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="family-management">
                        <div className="section-header-inline">
                            <h2>Family Members</h2>
                        </div>
                        <div className="kids-list">
                            <div className="kid-row parent-row">
                                <div className="kid-info">
                                    <span className="kid-icon">👑</span>
                                    <div className="kid-details">
                                        <strong>{user.username} (You)</strong>
                                        <span>Household Owner</span>
                                    </div>
                                </div>
                                <button className="btn-secondary btn-small" onClick={() => { setResetTarget({ id: user.user_id, username: user.username }); setIsResetModalOpen(true); }}>
                                    Change Admin Password
                                </button>
                            </div>

                            {kids.map(kid => (
                                <div key={kid.ID || kid.id} className="kid-row">
                                    <div className="kid-info">
                                        <span className="kid-icon">{kid.Avatar || kid.avatar || '👶'}</span>
                                        <div className="kid-details">
                                            <strong>{kid.DisplayName}</strong>
                                            <span className="kid-balance">@{kid.Username} • Balance: ${((kid.balance || 0) / 100).toFixed(2)}</span>
                                        </div>
                                    </div>
                                    <div className="kid-actions">
                                        <button
                                            className="btn-bonus btn-small"
                                            onClick={() => {
                                                setBonusTarget({ id: kid.ID || kid.id, name: kid.DisplayName || kid.Username });
                                                setIsBonusModalOpen(true);
                                            }}
                                            style={{ marginRight: '8px', background: '#10b981', color: 'white', border: 'none' }}
                                        >
                                            🎁 Award Bonus
                                        </button>
                                        <button className="btn-secondary btn-small" onClick={() => { setResetTarget({ id: kid.ID || kid.id, username: kid.Username }); setIsResetModalOpen(true); }}>
                                            Reset Password
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {goals.length > 0 && (
                        <section className="family-management">
                            <div className="section-header-inline">
                                <h2>🎯 Savings Goals</h2>
                            </div>
                            <div className="goals-list">
                                {goals.map(goal => {
                                    const progress = Math.min((goal.currentBalance / goal.target_amount) * 100, 100);
                                    const isComplete = goal.currentBalance >= goal.target_amount;

                                    return (
                                        <div key={goal.id} className="goal-card">
                                            <div className="goal-header">
                                                <div className="goal-info">
                                                    <h3>{goal.title}</h3>
                                                    <span className="goal-kid">👤 {goal.kidName}</span>
                                                </div>
                                                <div className="goal-amount">
                                                    <span className="goal-current">${(goal.currentBalance / 100).toFixed(2)}</span>
                                                    <span className="goal-separator"> / </span>
                                                    <span className="goal-target">${(goal.target_amount / 100).toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <div className="goal-progress-container">
                                                <div className="goal-progress-bar" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} aria-label={`${goal.title} progress: ${progress.toFixed(0)}%`}>
                                                    <div
                                                        className={`goal-progress-fill ${isComplete ? 'complete' : ''}`}
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                </div>
                                                <span className="goal-progress-text">
                                                    {isComplete ? '✅ Goal Reached!' : `${progress.toFixed(0)}% Complete`}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {pendingSubmissions > 0 && (
                        <section className="submissions-section">
                            <h2>🎯 Pending Chore Submissions ({pendingSubmissions})</h2>
                            <div className="submissions-grid">
                                {submissions.filter(s => s.status === 'pending').map(submission => {
                                    const kid = kids.find(k => k.id === submission.kid_user_id);
                                    const chore = challenges.find(c => c.id === submission.challenge_id);
                                    return (
                                        <div key={submission.id} className="submission-card">
                                            <div className="submission-header">
                                                <h3>{chore?.title || 'Chore Submission'}</h3>
                                                <span className="submission-status pending">Pending</span>
                                            </div>
                                            <p className="submission-kid">
                                                <strong>By:</strong> {kid?.DisplayName || 'Unknown'} {kid?.Avatar || kid?.avatar || '👶'}
                                            </p>
                                            {chore?.description && (
                                                <p className="submission-description">
                                                    <strong>Task:</strong> {chore.description}
                                                </p>
                                            )}
                                            {chore?.reward_amount && (
                                                <p className="submission-reward">
                                                    <strong>Reward:</strong> ${(chore.reward_amount / 100).toFixed(2)}
                                                </p>
                                            )}
                                            {chore?.created_at && (
                                                <p className="submission-assigned">
                                                    <strong>Assigned:</strong> {new Date(chore.created_at).toLocaleString()}
                                                </p>
                                            )}
                                            {submission.notes && (
                                                <p className="submission-notes">
                                                    <strong>Kid's Note:</strong> "{submission.notes}"
                                                </p>
                                            )}
                                            <p className="submission-time">
                                                <strong>Submitted:</strong> {new Date(submission.submitted_at).toLocaleString()}
                                            </p>
                                            <div className="submission-actions">
                                                <button
                                                    className="btn-approve"
                                                    onClick={() => handleApproveSubmission(submission.id)}
                                                    aria-label={`Approve and pay ${(chore?.reward_amount / 100).toFixed(2)} for ${chore?.title || 'chore'}`}
                                                >
                                                    ✓ Approve & Pay ${(chore?.reward_amount / 100).toFixed(2)}
                                                </button>
                                                <button
                                                    className="btn-reject"
                                                    onClick={() => handleRejectSubmission(submission.id)}
                                                    aria-label={`Reject submission for ${chore?.title || 'chore'}`}
                                                >
                                                    ✗ Reject
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {kids.length > 0 && (
                        <section className="family-management">
                            <AllowanceManager householdId={user.tenant_id} />
                        </section>
                    )}

                    <section className="family-management">
                        <SmartStore userRole="parent" />
                    </section>

                    {/* Settings Section */}
                    <section className="dashboard-section">
                        <h2>⚙️ Settings</h2>

                        <div className="settings-tabs">
                            <button
                                className={`settings-tab ${activeSettingsTab === 'general' ? 'active' : ''}`}
                                onClick={() => setActiveSettingsTab('general')}
                            >
                                General
                            </button>
                            <button
                                className={`settings-tab ${activeSettingsTab === 'streaks' ? 'active' : ''}`}
                                onClick={() => setActiveSettingsTab('streaks')}
                            >
                                🔥 Streak Rewards
                            </button>
                            <button
                                className={`settings-tab ${activeSettingsTab === 'emails' ? 'active' : ''}`}
                                onClick={() => setActiveSettingsTab('emails')}
                            >
                                📧 Email Templates
                            </button>
                            {user.role === 'admin' && (
                                <button
                                    className={`settings-tab ${activeSettingsTab === 'admin' ? 'active' : ''}`}
                                    onClick={() => setActiveSettingsTab('admin')}
                                >
                                    🛠️ Admin Quests
                                </button>
                            )}
                        </div>

                        <div className="settings-content">
                            {activeSettingsTab === 'general' && (
                                <div className="settings-general">
                                    <div className="email-preferences-card" style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                        <h3 style={{ marginTop: 0, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '1.5rem' }}>📧</span> Email Notifications
                                        </h3>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                <div>
                                                    <strong style={{ display: 'block', fontSize: '1.05rem', color: '#1e293b', marginBottom: '4px' }}>Weekly Digest</strong>
                                                    <span style={{ fontSize: '0.9rem', color: '#64748b' }}>Receive a weekly summary of your family's chores and DadBucks earned</span>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={emailPrefs.weekly_digest}
                                                    onChange={(e) => setEmailPrefs({ ...emailPrefs, weekly_digest: e.target.checked })}
                                                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                                />
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                <div>
                                                    <strong style={{ display: 'block', fontSize: '1.05rem', color: '#1e293b', marginBottom: '4px' }}>Chore Approval Alerts</strong>
                                                    <span style={{ fontSize: '0.9rem', color: '#64748b' }}>Get notified instantly when your child submits a chore for approval</span>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={emailPrefs.chore_reminders}
                                                    onChange={(e) => setEmailPrefs({ ...emailPrefs, chore_reminders: e.target.checked })}
                                                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                                />
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                <div>
                                                    <strong style={{ display: 'block', fontSize: '1.05rem', color: '#1e293b', marginBottom: '4px' }}>Marketing & Offers</strong>
                                                    <span style={{ fontSize: '0.9rem', color: '#64748b' }}>Updates on new DadBucks features and special premium offers</span>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={emailPrefs.marketing}
                                                    onChange={(e) => setEmailPrefs({ ...emailPrefs, marketing: e.target.checked })}
                                                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                                />
                                            </div>
                                        </div>

                                        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                                            <button
                                                className="btn-primary"
                                                disabled={isSavingPrefs}
                                                onClick={async () => {
                                                    setIsSavingPrefs(true);
                                                    try {
                                                        await axios.post('/api/v1/email/preferences', {
                                                            weekly_digest: emailPrefs.weekly_digest,
                                                            chore_reminders: emailPrefs.chore_reminders
                                                        });
                                                        alert('Email preferences saved successfully!');
                                                    } catch (err) {
                                                        console.error('Failed to save email preferences:', err);
                                                        alert('Failed to save email preferences.');
                                                    } finally {
                                                        setIsSavingPrefs(false);
                                                    }
                                                }}
                                                style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: '600', opacity: isSavingPrefs ? 0.7 : 1 }}
                                            >
                                                {isSavingPrefs ? 'Saving...' : 'Save Preferences'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Data Export - GDPR Article 20 */}
                                    <div className="email-preferences-card" style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginTop: '16px' }}>
                                        <h3 style={{ marginTop: 0, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '1.5rem' }}>📦</span> Your Data
                                        </h3>
                                        <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '16px' }}>
                                            Download a copy of all your family's DadBucks data, including profiles, balances, chores, goals, and transaction history.
                                        </p>
                                        <button
                                            id="data-export-button"
                                            className="btn-primary"
                                            onClick={async () => {
                                                try {
                                                    const response = await axios.get('/api/v1/users/export', {
                                                        responseType: 'blob'
                                                    });
                                                    const blob = new Blob([response.data], { type: 'application/json' });
                                                    const url = window.URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = `dadbucks_export_${new Date().toISOString().split('T')[0]}.json`;
                                                    document.body.appendChild(a);
                                                    a.click();
                                                    document.body.removeChild(a);
                                                    window.URL.revokeObjectURL(url);
                                                } catch (err) {
                                                    console.error('Failed to export data:', err);
                                                    alert('Failed to download data. Please try again.');
                                                }
                                            }}
                                            style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: '600' }}
                                        >
                                            📥 Download My Data
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeSettingsTab === 'streaks' && (
                                <StreakRewardSettings householdId={user.tenant_id} />
                            )}

                            {activeSettingsTab === 'emails' && (
                                <div style={{ padding: '4px 0' }}>
                                    <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1f2937' }}>📧 Customize Your Email Templates</h3>
                                    <EmailTemplateEditor />
                                </div>
                            )}

                            {activeSettingsTab === 'admin' && user.role === 'admin' && (
                                <div style={{ padding: '4px 0' }}>
                                    <AdminQuestDashboard />
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>

            {isAddChildModalOpen && (
                <div className="modal-overlay" onClick={() => {
                    setIsAddChildModalOpen(false);
                    setGeneratedCredentials(null);
                }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        {generatedCredentials ? (
                            <>
                                <h2>✅ Kid Added Successfully!</h2>
                                <div className="credentials-display">
                                    <p className="credentials-warning">⚠️ Please save these login credentials:</p>
                                    <div className="credentials-box">
                                        <p><strong>Name:</strong> {generatedCredentials.name}</p>
                                        <p><strong>Username:</strong> {generatedCredentials.username}</p>
                                        <p><strong>Password:</strong> {generatedCredentials.password}</p>
                                    </div>
                                    <p className="credentials-note">You can change the password later in Settings.</p>
                                </div>
                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        onClick={() => {
                                            setIsAddChildModalOpen(false);
                                            setGeneratedCredentials(null);
                                        }}
                                    >
                                        Done
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={() => {
                                            setGeneratedCredentials(null);
                                            setChildFormData({ name: '', username: '', password: '', coppaConsent: false });
                                        }}
                                    >
                                        Add Another Kid
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2>Add a New Family Member</h2>
                                <p className="modal-subtitle">Create a secure sub-account for your child.</p>
                                <form onSubmit={handleAddChild}>
                                    <AvatarPicker
                                        selectedAvatar={childFormData.avatar}
                                        onSelect={(avatar) => setChildFormData({ ...childFormData, avatar })}
                                    />
                                    <div className="input-group">
                                        <label>Child's Name</label>
                                        <input
                                            type="text"
                                            value={childFormData.name}
                                            onChange={(e) => setChildFormData({ ...childFormData, name: e.target.value })}
                                            required
                                            placeholder="Enter child's name"
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>Username (Optional)</label>
                                        <input
                                            type="text"
                                            value={childFormData.username}
                                            onChange={(e) => setChildFormData({ ...childFormData, username: e.target.value })}
                                            placeholder="Leave blank to auto-generate"
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>Password (Optional)</label>
                                        <input
                                            type="text"
                                            value={childFormData.password}
                                            onChange={(e) => setChildFormData({ ...childFormData, password: e.target.value })}
                                            placeholder="Leave blank to auto-generate"
                                        />
                                    </div>
                                    <small className="input-hint">A secure password will be generated if left blank</small>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', margin: '16px 0', padding: '14px', background: '#FFF7ED', borderRadius: '10px', border: '1px solid #FDBA74' }}>
                                        <input
                                            type="checkbox"
                                            id="coppa-consent"
                                            checked={childFormData.coppaConsent}
                                            onChange={(e) => setChildFormData({ ...childFormData, coppaConsent: e.target.checked })}
                                            required
                                            style={{ marginTop: '4px', width: '18px', height: '18px', flexShrink: 0, accentColor: '#4338CA' }}
                                        />
                                        <label htmlFor="coppa-consent" style={{ fontSize: '0.85rem', lineHeight: '1.4', color: '#78350F', cursor: 'pointer' }}>
                                            I am the parent or legal guardian and I give consent for my child to use DadBucks, including the collection of limited data necessary to operate the service, in accordance with <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#4338CA' }}>our Privacy Policy</a> and COPPA regulations.
                                        </label>
                                    </div>
                                    {childError && <p className="error-message">{childError}</p>}
                                    <div className="modal-actions">
                                        <button type="button" className="btn-secondary" onClick={() => setIsAddChildModalOpen(false)}>Cancel</button>
                                        <button type="submit" className="btn-primary" disabled={isSubmitting}>
                                            {isSubmitting ? 'Creating...' : 'Create Child Account'}
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}

            {isResetModalOpen && (
                <div className="modal-overlay" onClick={() => { setIsResetModalOpen(false); setNewPassword(''); setConfirmPassword(''); setPasswordError(''); }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h2>Reset Password</h2>
                        <p className="modal-subtitle">Set a new secure password for <strong>{resetTarget?.username}</strong></p>
                        {passwordError && <p style={{ color: '#ef4444', fontSize: '0.9rem', marginBottom: '12px' }}>{passwordError}</p>}
                        <form onSubmit={handleResetPassword}>
                            <div className="input-group">
                                <label>New Password</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    minLength={6}
                                    autoFocus
                                />
                            </div>
                            <div className="input-group">
                                <label>Confirm Password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    minLength={6}
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => { setIsResetModalOpen(false); setNewPassword(''); setConfirmPassword(''); setPasswordError(''); }}>Cancel</button>
                                <button type="submit" className="btn-primary">Update Password</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isCreateChoreModalOpen && (
                <div className="modal-overlay" onClick={() => setIsCreateChoreModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h2>Create a New Chore</h2>
                        <p className="modal-subtitle">Set up a task for your kids to earn DadBucks.</p>
                        <form onSubmit={handleCreateChore}>
                            <div className="input-group">
                                <label>Chore Title</label>
                                <input
                                    type="text"
                                    value={choreFormData.title}
                                    onChange={(e) => setChoreFormData({ ...choreFormData, title: e.target.value })}
                                    placeholder="e.g. Clean the Kitchen"
                                    required
                                />
                            </div>
                            <div className="input-group">
                                <label>Reward Amount (DadBucks)</label>
                                <input
                                    type="number"
                                    value={choreFormData.reward_amount}
                                    onChange={(e) => setChoreFormData({ ...choreFormData, reward_amount: parseInt(e.target.value) })}
                                    required
                                />
                            </div>
                            <div className="input-group">
                                <label>Frequency</label>
                                <select
                                    value={choreFormData.recurrence}
                                    onChange={(e) => setChoreFormData({ ...choreFormData, recurrence: e.target.value })}
                                >
                                    <option value="none">One-time</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                </select>
                            </div>
                            {challenges && challenges.length > 0 && (
                                <div className="input-group">
                                    <label>Prerequisite Quest (Optional)</label>
                                    <select
                                        value={choreFormData.depends_on_quest_id}
                                        onChange={(e) => setChoreFormData({ ...choreFormData, depends_on_quest_id: e.target.value })}
                                    >
                                        <option value="">None - Unlocked immediately</option>
                                        {challenges.filter(c => c.status === 'active').map(c => (
                                            <option key={c.id} value={c.id}>{c.title}</option>
                                        ))}
                                    </select>
                                    <p className="input-hint">This chore will remain locked until the child finishes the prerequisite.</p>
                                </div>
                            )}
                            {kids.length > 0 && (
                                <div className="input-group">
                                    <label>Assign to Kids</label>
                                    <p className="input-hint">Select which kids should do this chore. Leave all unchecked to assign to everyone.</p>
                                    <div className="kid-selector-list">
                                        {kids.map(kid => {
                                            const kidId = (kid.ID || kid.id).toString();
                                            const isSelected = choreFormData.kid_ids.includes(kidId);
                                            return (
                                                <label key={kidId} className="kid-checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleKidToggle(kidId)}
                                                    />
                                                    <span>{kid.DisplayName || kid.Username}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setIsCreateChoreModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn-primary">Create Chore</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isSetGoalModalOpen && (
                <div className="modal-overlay" onClick={() => setIsSetGoalModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h2>Set a Savings Goal</h2>
                        <p className="modal-subtitle">What is your child working towards?</p>
                        <form onSubmit={handleSetGoal}>
                            <div className="input-group">
                                <label>Select Child</label>
                                <select
                                    value={goalFormData.kid_user_id}
                                    onChange={(e) => setGoalFormData({ ...goalFormData, kid_user_id: e.target.value })}
                                    required
                                >
                                    <option value="">Choose a child...</option>
                                    {kids.map(kid => (
                                        <option key={kid.id} value={kid.id}>{kid.DisplayName}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="input-group">
                                <label>Goal Title</label>
                                <input
                                    type="text"
                                    value={goalFormData.title}
                                    onChange={(e) => setGoalFormData({ ...goalFormData, title: e.target.value })}
                                    placeholder="e.g. New LEGO Set"
                                    required
                                />
                            </div>
                            <div className="input-group">
                                <label>Target Amount ($)</label>
                                <input
                                    type="number"
                                    value={goalFormData.target_amount}
                                    onChange={(e) => setGoalFormData({ ...goalFormData, target_amount: parseInt(e.target.value) })}
                                    required
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setIsSetGoalModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn-primary">Set Goal</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isBonusModalOpen && bonusTarget && (
                <div className="modal-overlay" onClick={() => { setIsBonusModalOpen(false); setBonusError(''); }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <h2>🎁 Surprise Bonus for {bonusTarget.name}</h2>
                        <p className="modal-subtitle">Reward great behavior with a surprise!</p>

                        {/* Quick Reason Buttons */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Quick Reasons</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {[
                                    { emoji: '🌟', text: 'Great attitude today!' },
                                    { emoji: '🤝', text: 'Helping without being asked' },
                                    { emoji: '📚', text: 'Extra effort on homework' },
                                    { emoji: '🧹', text: 'Above and beyond on chores' },
                                    { emoji: '❤️', text: 'Being kind to siblings' },
                                    { emoji: '💪', text: 'Showing responsibility' },
                                    { emoji: '✨', text: 'Just because!' },
                                ].map(({ emoji, text }) => (
                                    <button
                                        key={text}
                                        type="button"
                                        onClick={() => setBonusReason(text)}
                                        style={{
                                            padding: '8px 12px',
                                            borderRadius: '20px',
                                            border: bonusReason === text ? '2px solid #10b981' : '1px solid #e5e7eb',
                                            background: bonusReason === text ? '#ecfdf5' : 'white',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {emoji} {text}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <form onSubmit={handleAwardBonus}>
                            <div className="input-group">
                                <label>Bonus Amount ($)</label>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                    {[1, 2, 5, 10].map(amt => (
                                        <button
                                            key={amt}
                                            type="button"
                                            onClick={() => setBonusAmount(amt.toString())}
                                            style={{
                                                padding: '8px 16px',
                                                borderRadius: '8px',
                                                border: parseFloat(bonusAmount) === amt ? '2px solid #10b981' : '1px solid #e5e7eb',
                                                background: parseFloat(bonusAmount) === amt ? '#ecfdf5' : 'white',
                                                cursor: 'pointer',
                                                fontWeight: '600',
                                                fontSize: '1rem'
                                            }}
                                        >
                                            ${amt}
                                        </button>
                                    ))}
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={bonusAmount}
                                    onChange={(e) => setBonusAmount(e.target.value)}
                                    placeholder="Or enter custom amount"
                                    required
                                />
                            </div>
                            <div className="input-group">
                                <label>Reason (or type your own)</label>
                                <input
                                    type="text"
                                    value={bonusReason}
                                    onChange={(e) => setBonusReason(e.target.value)}
                                    placeholder="e.g. Being extra helpful today!"
                                    required
                                />
                            </div>
                            {bonusError && <p className="error-text">{bonusError}</p>}
                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => { setIsBonusModalOpen(false); setBonusError(''); }}>Cancel</button>
                                <button type="submit" className="btn-primary" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                                    🎉 Give Surprise Bonus
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Feedback Modal */}
            {isFeedbackModalOpen && (
                <div className="modal-overlay" onClick={() => { setIsFeedbackModalOpen(false); setFeedbackError(''); setFeedbackSuccess(''); }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h2>💬 Share Your Feedback</h2>
                        <p className="modal-subtitle">Help us make DadBucks better for your family</p>
                        <form onSubmit={async (e) => {
                            e.preventDefault();
                            setFeedbackError('');
                            setFeedbackSuccess('');
                            if (!feedbackTitle.trim()) {
                                setFeedbackError('Please enter a title');
                                return;
                            }
                            try {
                                await axios.post('/api/v1/feedback', {
                                    type: feedbackType,
                                    title: feedbackTitle.trim(),
                                    description: feedbackDescription.trim()
                                });
                                setFeedbackSuccess('Thanks for your feedback! We\'ll review it soon.');
                                setFeedbackTitle('');
                                setFeedbackDescription('');
                                setTimeout(() => {
                                    setIsFeedbackModalOpen(false);
                                    setFeedbackSuccess('');
                                }, 2000);
                            } catch (err) {
                                setFeedbackError(err.response?.data || 'Failed to submit feedback');
                            }
                        }}>
                            <div className="input-group">
                                <label>Type</label>
                                <select value={feedbackType} onChange={(e) => setFeedbackType(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    <option value="feature">💡 Feature Request</option>
                                    <option value="bug">🐛 Bug Report</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label>Title</label>
                                <input
                                    type="text"
                                    value={feedbackTitle}
                                    onChange={(e) => setFeedbackTitle(e.target.value)}
                                    placeholder={feedbackType === 'bug' ? 'e.g. Balance not updating' : 'e.g. Add dark mode'}
                                    required
                                />
                            </div>
                            <div className="input-group">
                                <label>Description (optional)</label>
                                <textarea
                                    value={feedbackDescription}
                                    onChange={(e) => setFeedbackDescription(e.target.value)}
                                    placeholder="Tell us more details..."
                                    rows={3}
                                    style={{ padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', resize: 'vertical' }}
                                />
                            </div>
                            {feedbackError && <p className="error-text">{feedbackError}</p>}
                            {feedbackSuccess && <p style={{ color: '#10b981', fontWeight: 600 }}>{feedbackSuccess}</p>}
                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => { setIsFeedbackModalOpen(false); setFeedbackError(''); }}>Cancel</button>
                                <button type="submit" className="btn-primary">Submit Feedback</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Email Preferences */}
            <EmailPreferences />

            {/* Floating Feedback Button */}
            <button
                onClick={() => setIsFeedbackModalOpen(true)}
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    zIndex: 1000,
                    transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.boxShadow = '0 6px 16px rgba(99, 102, 241, 0.5)';
                }}
                onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.4)';
                }}
                title="Give Feedback"
            >
                💬
            </button>
        </div>
    );
};

export default ParentDashboard;
